import { useEffect, useState, useSyncExternalStore } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button, buttonProps, LoadingSkeleton } from "@a2z/ui";
import { useLocale } from "@/app/locale";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { isIosCompanionApp } from "@/lib/native/runtime";
import {
  getPrivateSessionState,
  subscribePrivateSession,
} from "@/lib/api/private-session";
import {
  metaLinkPath,
  parseMetaLinkProvider,
  withAuthReturnPath,
} from "@/lib/auth/safe-return";
import { inspectPrivateBinding } from "@/shared/services/sessionChannel";

/** Resolve the real session before offering explicit profile linking. */
export function MetaLinkPage() {
  const locale = useLocale();
  const t = useTranslations();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const provider = isIosCompanionApp() ? null : parseMetaLinkProvider(params);
  const session = useSyncExternalStore(
    subscribePrivateSession,
    getPrivateSessionState,
  );
  const [failed, setFailed] = useState<{
    attempt: number;
    generation: number;
    provider: string;
  } | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!provider || session.changing) return;
    let active = true;
    const generation = session.generation;
    const current = () =>
      active &&
      !getPrivateSessionState().changing &&
      getPrivateSessionState().generation === generation;
    void inspectPrivateBinding()
      .then((binding) => {
        if (!current()) return;
        const next = metaLinkPath(locale, provider);
        navigate(
          !binding
            ? withAuthReturnPath(`/${locale}/login`, next)
            : binding.mustChangePassword
              ? withAuthReturnPath(`/${locale}/change-password`, next)
              : `/${locale}/${encodeURIComponent(binding.seller.storeSlug)}/profile?linkProvider=${provider}`,
          { replace: true },
        );
      })
      .catch(() => {
        if (current()) setFailed({ attempt, generation, provider });
      });
    return () => {
      active = false;
    };
  }, [
    attempt,
    locale,
    navigate,
    provider,
    session.changing,
    session.generation,
  ]);

  const failure =
    !provider ||
    (failed?.attempt === attempt &&
      failed.generation === session.generation &&
      failed.provider === provider &&
      !session.changing);
  return (
    <main className="grid min-h-svh place-items-center bg-[color:var(--canvas)] p-6">
      <section className="w-full max-w-md space-y-5 rounded-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] p-6">
        <h1 className="text-xl font-semibold">{t(keys.meta.title)}</h1>
        {failure ? (
          <>
            <p
              role="alert"
              className="text-sm leading-7 text-[color:var(--text-muted)]"
            >
              {t(
                provider
                  ? keys.meta.callbackSessionUnavailable
                  : keys.meta.callbackFailed,
              )}
            </p>
            {provider ? (
              <Button
                variant="primary"
                size="touch"
                fullWidth
                onClick={() => setAttempt((value) => value + 1)}
              >
                {t(keys.common.retry)}
              </Button>
            ) : (
              <Link
                to={`/${locale}/login`}
                {...buttonProps({
                  variant: "primary",
                  size: "touch",
                  fullWidth: true,
                })}
              >
                {t(keys.meta.callbackReturnLogin)}
              </Link>
            )}
          </>
        ) : (
          <div role="status" aria-busy="true">
            <span className="sr-only">{t(keys.common.loading)}</span>
            <LoadingSkeleton rows={3} />
          </div>
        )}
      </section>
    </main>
  );
}
